import { useSelector } from 'react-redux';
import { CurrentUserSessionHelper } from 'utility/CurrentUserSessionHelper';

/**
 * Returns the current customer's cognito group UUID (the first UUID in the
 * user's cognito groups). Prefer this in function components over a raw
 * useSelector on loginReducer.groups[0] — it is robust against role groups
 * (admin, roadmin, beta, internal, ROLE_*, etc.) landing at index 0.
 *
 * @returns {String|null} - The current customer's cognito group UUID, or null.
 */
export const useCurrentCustomerId = () => {
    const groups = useSelector((state) => state.loginReducer?.groups);
    return CurrentUserSessionHelper.getCurrentCustomerId(groups);
};

/**
 * Returns true if the current user's cognito groups include the given group,
 * or any of the given groups when passed an array. Default semantics for the
 * array form is "any of" — if you need "all of", introduce a sibling hook
 * rather than overloading this one.
 *
 * @param {string | string[]} groupOrGroups
 * @returns {boolean}
 *
 * @example
 *   const isBeta = useHasCognitoGroup("beta");
 *   const isAdmin = useHasCognitoGroup(["admin", "roadmin"]);
 *   const isJourneyManager = useHasCognitoGroup(ROLE_JOURNEY_MANAGER_BETA_USER);
 */
export const useHasCognitoGroup = (groupOrGroups) => {
    const groups = useSelector((state) => state.loginReducer?.groups);
    if (!groups) return false;
    if (Array.isArray(groupOrGroups)) {
        return groupOrGroups.some((g) => groups.includes(g));
    }
    return groups.includes(groupOrGroups);
};
